import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  Check,
  ChevronRight,
  Clock3,
  Copy,
  Package,
  PackageCheck,
  Search as SearchIcon,
  Truck,
  X,
  XCircle,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AccountSidebar from '../components/AccountSidebar';
import DecorLeaf from '../components/DecorLeaf';
import ErrorState from '../components/ErrorState';
import ProductImage from '../components/ProductImage';
import EmptyState from '../components/EmptyState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as ordersApi from '../api/endpoints/orders';
import {
  buildOrderProgress,
  isTerminalFailure,
  resolveOrderStatus,
  statusLabel,
  statusTone,
  supportsLiveTracking,
} from '../config/orderStatus';
import { focusRing, homeRail } from '../styles/ui';

// One request, newest first. The API's own `status` parameter only accepts
// all/active/past, so the per-status chips below filter this loaded page
// client-side rather than inventing query values the backend would reject.
const PAGE_LIMIT = 50;

const THUMBNAILS_SHOWN = 3;

// Presentation-only line under the timeline, chosen from the canonical status.
const STATUS_NOTE = {
  placed: "We've received your order and it's being confirmed.",
  confirmed: 'Your order is confirmed and will be prepared soon.',
  preparing: "We're preparing your fresh items with care.",
  packed: 'Your order is packed and ready to go.',
  allocated: "We're preparing your fresh items with care.",
  partially_allocated: "We're preparing your fresh items with care.",
  assigned: 'Your order has been handed to a delivery partner.',
  out_for_delivery: 'Your order is on its way.',
  delivered: 'Your order has been delivered successfully.',
  cancelled: 'This order was cancelled.',
  failed: "We couldn't complete this order.",
};

const formatOrderDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} • ${date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const formatStepTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}, ${date.toLocaleTimeString(
    [],
    { hour: '2-digit', minute: '2-digit' }
  )}`;
};

const toDayStart = (value) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Compact horizontal tracker for wide cards; a one-line summary with a thin
// segmented bar where five labels would not fit. Both come from the same
// buildOrderProgress data, and a failed/cancelled order shows neither.
const OrderProgress = ({ order }) => {
  const progress = buildOrderProgress(order);

  if (progress.failed) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
            <X size={12} aria-hidden="true" />
          </span>
          <span className="text-[13px] font-semibold text-red-700">Stopped</span>
          {progress.at && <span className="text-xs text-ink-secondary">{formatStepTime(progress.at)}</span>}
        </div>
        {/* Only the stages this order genuinely reached before it stopped — a
            cancelled order is never drawn as if it were still on its way. */}
        {progress.steps.length > 0 && (
          <p className="text-xs text-ink-secondary">Reached {progress.steps.map((step) => step.label).join(' · ')}</p>
        )}
      </div>
    );
  }

  const currentIndex = Math.max(progress.steps.findIndex((step) => step.state === 'current'), 0);
  const current = progress.steps[currentIndex];

  return (
    <>
      <ol className="hidden xl:flex items-start" aria-label="Order progress">
        {progress.steps.map((step, index) => {
          const done = step.state === 'complete';
          const isCurrent = step.state === 'current';
          return (
            <li key={step.id} className="flex-1 min-w-0 flex flex-col items-center relative">
              {index > 0 && (
                <span
                  className={`absolute top-[8px] right-1/2 left-[-50%] h-[2px] ${done || isCurrent ? 'bg-[#2E8B47]' : 'bg-[#E3E8E3]'}`}
                  aria-hidden="true"
                />
              )}
              <span
                className={`relative z-10 w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 ${
                  done
                    ? 'bg-[#2E8B47] text-white'
                    : isCurrent
                    ? 'bg-white border-[3px] border-[#2E8B47] ring-4 ring-[#2E8B47]/15'
                    : 'bg-white border-2 border-[#D6DDD6]'
                }`}
              >
                {done && <Check size={11} strokeWidth={3} aria-hidden="true" />}
              </span>
              <span
                className={`mt-1.5 text-[11px] leading-tight text-center ${
                  isCurrent ? 'font-bold text-emerald-900' : done ? 'font-semibold text-emerald-950' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
              {step.at && (
                <span className="text-[10px] text-ink-secondary leading-tight text-center mt-0.5">{formatStepTime(step.at)}</span>
              )}
            </li>
          );
        })}
      </ol>
      <div className="xl:hidden" aria-hidden="true">
        <div className="flex items-center justify-between gap-3 text-[12px]">
          <span className="font-semibold text-emerald-900">
            Step {currentIndex + 1} of {progress.steps.length} · {current?.label}
          </span>
          {current?.at && <span className="text-ink-secondary">{formatStepTime(current.at)}</span>}
        </div>
        <div className="mt-1.5 flex gap-1">
          {progress.steps.map((step) => (
            <span
              key={step.id}
              className={`h-1.5 flex-1 rounded-full ${step.state === 'upcoming' ? 'bg-[#E3E8E3]' : 'bg-[#2E8B47]'}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};

// Up to three real item photos (shared fallback for missing/broken ones) and
// the real remainder count.
const OrderThumbnails = ({ order }) => {
  const items = Array.isArray(order.items) ? order.items : [];
  const shown = items.slice(0, THUMBNAILS_SHOWN);
  const remaining = Math.max(items.length - shown.length, 0);

  if (shown.length === 0) return null;

  return (
    <div className="flex items-center">
      {shown.map((item, index) => (
        <span
          key={item.saleItemId || item._id || index}
          className={`w-[52px] h-[52px] rounded-xl overflow-hidden bg-[#F4F7F3] border-2 border-white shadow-[0_2px_8px_-4px_rgba(15,51,24,0.35)] shrink-0 ${index > 0 ? '-ml-2.5' : ''}`}
          title={item.productName || ''}
        >
          <ProductImage src={item.imageUrl} alt="" size="thumb" />
        </span>
      ))}
      {remaining > 0 && (
        <span className="ml-2 h-[30px] px-2.5 rounded-full bg-[#F1FAEF] border border-[#DFF3DA] text-[11.5px] font-bold text-emerald-800 flex items-center shrink-0">
          +{remaining} item{remaining === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
};

const itemSummary = (order) => {
  const items = Array.isArray(order.items) ? order.items : [];
  if (items.length === 0) return '';
  const first = items[0]?.productName;
  if (!first) return `${items.length} item${items.length === 1 ? '' : 's'}`;
  return items.length === 1 ? first : `${first} and ${items.length - 1} more`;
};

const OrderCard = ({ order }) => {
  const id = order._id || order.saleId;
  const status = resolveOrderStatus(order);
  const canTrack = supportsLiveTracking(order);
  const canCancel = Boolean(order.availableActions?.canCancel);
  const failed = isTerminalFailure(status);
  const note = STATUS_NOTE[status];
  const [copied, setCopied] = useState(false);
  const orderNumber = order.invoiceNumber || `Order ${id}`;
  const summary = itemSummary(order);

  const copyOrderNumber = async () => {
    try {
      await navigator.clipboard?.writeText(order.invoiceNumber || String(id));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard unavailable — nothing to do.
    }
  };

  return (
    <li className="bg-white rounded-[18px] border border-[#E4EDE4] shadow-[0_8px_22px_-18px_rgba(15,51,24,0.35)] transition-shadow hover:shadow-[0_14px_30px_-20px_rgba(15,51,24,0.45)]">
      {/* Identity · status · total */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-4 sm:px-5 pt-4 pb-3 border-b border-[#EEF3EE]">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-[#E4F3E5] text-emerald-700 flex items-center justify-center shrink-0">
            <Package size={19} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1 min-w-0">
              <p className="text-[15px] font-bold text-emerald-950 leading-tight truncate">{orderNumber}</p>
              {order.invoiceNumber && (
                <button
                  type="button"
                  onClick={copyOrderNumber}
                  aria-label={`Copy order number ${order.invoiceNumber}`}
                  className={`w-7 h-7 rounded-md text-emerald-800/70 hover:bg-emerald-50 flex items-center justify-center shrink-0 ${focusRing}`}
                >
                  {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
                </button>
              )}
            </div>
            <p className="text-[12px] text-ink-secondary mt-[2px]">{formatOrderDate(order.createdAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <span className={`inline-flex items-center h-7 px-3 rounded-full border text-[12px] font-bold ${statusTone(status)}`}>
            {statusLabel(status)}
          </span>
          <span className="text-[20px] font-extrabold text-emerald-950 leading-none">₹{order.grandTotal}</span>
        </div>
      </div>

      {/* Items · progress */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)] gap-4 xl:gap-6 items-center px-4 sm:px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <OrderThumbnails order={order} />
          {summary && <p className="text-[12.5px] text-[#4B5563] leading-snug line-clamp-2 min-w-0">{summary}</p>}
        </div>
        <div className="min-w-0">
          <OrderProgress order={order} />
        </div>
      </div>

      {/* Note · actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 sm:px-5 pb-4">
        {note ? (
          <p className={`flex items-center gap-1.5 text-[12.5px] min-w-0 ${failed ? 'text-red-700' : 'text-ink-secondary'}`}>
            {failed ? (
              <XCircle size={14} className="shrink-0" aria-hidden="true" />
            ) : status === 'delivered' ? (
              <Check size={14} className="text-emerald-700 shrink-0" aria-hidden="true" />
            ) : (
              <Clock3 size={14} className="text-emerald-700 shrink-0" aria-hidden="true" />
            )}
            <span className="truncate">{note}</span>
          </p>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2 shrink-0">
          {/* Cancellation is offered only where the backend says it is
              allowed for this order. There is no reorder flow in this app. */}
          {!canTrack && canCancel && (
            <Link
              to={`/orders/${id}/cancel`}
              state={{ order }}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center h-10 px-4 rounded-full text-red-600 text-[13px] font-bold whitespace-nowrap hover:bg-red-50 transition-colors ${focusRing}`}
            >
              Cancel Order
            </Link>
          )}
          {/* Tracking lives on the order's own page and only exists once the
              order is actually on its way. */}
          {canTrack && (
            <Link
              to={`/orders/${id}`}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-full border border-[#2E8B47] bg-white text-[#1E5A30] text-[13px] font-bold whitespace-nowrap hover:bg-emerald-50 ${focusRing}`}
            >
              <Truck size={14} aria-hidden="true" />
              Track Order
            </Link>
          )}
          <Link
            to={`/orders/${id}`}
            className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 h-10 px-5 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-[13px] font-bold whitespace-nowrap shadow-[0_6px_14px_-7px_rgba(46,139,71,0.7)] hover:brightness-105 ${focusRing}`}
          >
            View Details
            <ChevronRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </li>
  );
};

// Same three bands as a real card, so nothing jumps when orders arrive.
const OrderCardSkeleton = () => (
  <li className="bg-white rounded-[18px] border border-[#E4EDE4] animate-pulse">
    <div className="flex items-center justify-between gap-4 px-5 pt-4 pb-3 border-b border-[#EEF3EE]">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#EEF5EC]" />
        <span className="space-y-2">
          <span className="block h-4 w-36 rounded bg-[#EEF5EC]" />
          <span className="block h-3 w-28 rounded bg-[#F2F7F1]" />
        </span>
      </div>
      <span className="h-6 w-28 rounded-full bg-[#EEF5EC]" />
    </div>
    <div className="flex items-center gap-4 px-5 py-4">
      <span className="h-[52px] w-[140px] rounded-xl bg-[#EEF5EC]" />
      <span className="h-3 flex-1 rounded bg-[#F2F7F1]" />
    </div>
    <div className="flex justify-end px-5 pb-4">
      <span className="h-10 w-36 rounded-full bg-[#EEF5EC]" />
    </div>
  </li>
);

// Icons for the status chips, by canonical status; the label is the shared one.
const CHIP_ICON = {
  all: Package,
  delivered: PackageCheck,
  cancelled: XCircle,
  failed: XCircle,
  out_for_delivery: Truck,
};

const OrderHistoryPage = () => {
  const [state, setState] = useState({ status: 'loading', data: null, error: null, pagination: null });
  const [activeStatus, setActiveStatus] = useState('all');
  const [query, setQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateOpen, setDateOpen] = useState(false);

  usePageMetadata({
    title: 'My Orders | Fresh & Homely',
    description: 'Track, manage and reorder your Fresh & Homely essentials.',
    canonicalPath: '/orders',
  });

  const load = () => {
    setState({ status: 'loading', data: null, error: null, pagination: null });
    ordersApi
      .listOrders({ status: 'all', limit: PAGE_LIMIT })
      .then((response) =>
        setState({
          status: 'success',
          data: response.data,
          error: null,
          pagination: response.pagination || null,
        })
      )
      .catch((err) => setState({ status: 'error', data: null, error: err.message, pagination: null }));
  };

  useEffect(load, []);

  const orders = useMemo(() => (Array.isArray(state.data) ? state.data : []), [state.data]);

  // Chips are built from the statuses present in the loaded orders — never a
  // fixed list, so a lifecycle the backend adds shows up on its own.
  const statusChips = useMemo(() => {
    const counts = new Map();
    orders.forEach((order) => {
      const status = resolveOrderStatus(order);
      counts.set(status, (counts.get(status) || 0) + 1);
    });
    return [
      { id: 'all', label: 'All Orders', count: orders.length },
      ...[...counts.entries()].map(([status, count]) => ({
        id: status,
        label: statusLabel(status),
        count,
      })),
    ];
  }, [orders]);

  const visibleOrders = useMemo(() => {
    const term = query.trim().toLowerCase();
    const from = dateFrom ? toDayStart(dateFrom) : null;
    const to = dateTo ? toDayStart(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);

    return orders.filter((order) => {
      if (activeStatus !== 'all' && resolveOrderStatus(order) !== activeStatus) return false;
      if (term) {
        const haystack = `${order.invoiceNumber || ''} ${order._id || ''} ${order.saleId || ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (from || to) {
        const placed = order.createdAt ? new Date(order.createdAt) : null;
        if (!placed || Number.isNaN(placed.getTime())) return false;
        if (from && placed < from) return false;
        if (to && placed > to) return false;
      }
      return true;
    });
  }, [orders, activeStatus, query, dateFrom, dateTo]);

  const filtersActive = activeStatus !== 'all' || query.trim() || dateFrom || dateTo;
  const total = state.pagination?.total ?? orders.length;
  const hasMoreThanLoaded = total > orders.length;

  return (
    <>
      <Navbar />

      <main className="relative overflow-hidden pt-20 pb-12 bg-[linear-gradient(180deg,#F7FBF5_0%,#FFFFFF_35%,#F7FBF5_100%)]">
        <div className="pointer-events-none select-none absolute inset-0" aria-hidden="true">
          <DecorLeaf className="hidden xl:block absolute left-[12px] top-[110px] w-9 text-emerald-500/70 -rotate-[20deg]" />
          <DecorLeaf className="hidden xl:block absolute left-[6px] top-[330px] w-7 text-emerald-600/60 rotate-[24deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[40px] top-[120px] w-10 text-emerald-500/70 rotate-[14deg]" />
          <DecorLeaf className="hidden xl:block absolute right-[30px] top-[560px] w-8 text-emerald-600/60 -rotate-[26deg]" />
          <DecorLeaf className="hidden xl:block absolute left-[130px] bottom-[40px] w-8 text-emerald-500/70 rotate-[30deg]" />
        </div>

        <div className={`${homeRail} relative pt-[22px]`}>
          <div className="grid grid-cols-1 lg:grid-cols-[262px_minmax(0,1fr)] gap-6 xl:gap-[28px] items-start min-[1500px]:-mx-[46px]">
            <div className="lg:sticky lg:top-24">
              <AccountSidebar />
            </div>

            <div className="min-w-0">
              {/* Header */}
              <div className="relative">
                <nav aria-label="Breadcrumb">
                  <ol className="flex items-center gap-1.5 text-[12.5px] text-ink-secondary">
                    <li><Link to="/" className={`hover:text-emerald-700 ${focusRing}`}>Home</Link></li>
                    <li aria-hidden="true"><ChevronRight size={13} /></li>
                    <li><Link to="/account" className={`hover:text-emerald-700 ${focusRing}`}>My Account</Link></li>
                    <li aria-hidden="true"><ChevronRight size={13} /></li>
                    <li aria-current="page" className="font-medium text-emerald-800">My Orders</li>
                  </ol>
                </nav>
                <h1 className="serif mt-2 text-[34px] xl:text-[40px] font-bold text-emerald-950 leading-[1.05]">My Orders</h1>
                <p className="text-[14.5px] text-[#4B5563] mt-1.5">Track and manage your fresh essentials, all in one place.</p>
              </div>

              {/* Filters row */}
              <div className="mt-5 rounded-[18px] bg-white border border-[#E4EDE4] shadow-[0_8px_22px_-20px_rgba(15,51,24,0.35)] p-2.5 flex flex-col gap-2.5">
                {state.status === 'success' && orders.length > 0 && (
                  <ul className="flex gap-1.5 overflow-x-auto no-scrollbar min-w-0">
                    {statusChips.map((chip) => {
                      const Icon = CHIP_ICON[chip.id] || Package;
                      const active = activeStatus === chip.id;
                      return (
                        <li key={chip.id} className="shrink-0">
                          <button
                            type="button"
                            onClick={() => setActiveStatus(chip.id)}
                            aria-pressed={active}
                            className={`inline-flex items-center gap-2 h-10 pl-3 pr-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-colors ${focusRing} ${
                              active ? 'bg-[#1E7A3C] text-white shadow-[0_6px_14px_-8px_rgba(46,139,71,0.7)]' : 'text-emerald-950 hover:bg-[#F1FAEF]'
                            }`}
                          >
                            <Icon size={15} className={active ? 'text-white' : 'text-emerald-700'} aria-hidden="true" />
                            <span>{chip.label}</span>
                            <span className="sr-only"> ({chip.count})</span>
                            <span aria-hidden="true" className={`min-w-[24px] h-6 px-1.5 rounded-full text-[11.5px] font-bold flex items-center justify-center ${active ? 'bg-white/20 text-white' : 'bg-[#EEF5EC] text-emerald-800'}`}>
                              {chip.count}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="flex items-center gap-2 border-t border-[#EEF3EE] pt-2.5">
                  <div className="relative flex-1">
                    <SearchIcon
                      size={16}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      aria-hidden="true"
                    />
                    <input
                      type="text"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      aria-label="Search your orders by order number"
                      placeholder="Search by order number..."
                      className={`h-10 w-full pl-10 pr-4 rounded-full bg-[#F6FAF4] border border-[#E4EDE4] text-[13px] text-ink-primary placeholder:text-gray-400 outline-none focus:border-emerald-300 ${focusRing}`}
                    />
                  </div>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setDateOpen((open) => !open)}
                      aria-expanded={dateOpen}
                      className={`inline-flex items-center gap-2 h-10 px-4 rounded-full border text-[13px] font-semibold whitespace-nowrap ${dateFrom || dateTo ? 'bg-[#1E7A3C] border-[#1E7A3C] text-white' : 'bg-[#F6FAF4] border-[#E4EDE4] text-emerald-950'} ${focusRing}`}
                    >
                      <CalendarDays size={16} className={dateFrom || dateTo ? 'text-white' : 'text-emerald-700'} aria-hidden="true" />
                      {dateFrom || dateTo ? 'Date filtered' : 'Date Range'}
                    </button>
                    {dateOpen && (
                      <div className="absolute right-0 z-30 mt-2 w-72 rounded-2xl bg-white border border-emerald-100 shadow-floating p-4">
                        <label className="block text-xs font-semibold text-emerald-950 mb-1" htmlFor="orders-date-from">
                          From
                        </label>
                        <input
                          id="orders-date-from"
                          type="date"
                          value={dateFrom}
                          onChange={(event) => setDateFrom(event.target.value)}
                          className="w-full h-10 px-3 mb-3 rounded-xl border border-emerald-100 text-sm outline-none focus:border-emerald-300"
                        />
                        <label className="block text-xs font-semibold text-emerald-950 mb-1" htmlFor="orders-date-to">
                          To
                        </label>
                        <input
                          id="orders-date-to"
                          type="date"
                          value={dateTo}
                          onChange={(event) => setDateTo(event.target.value)}
                          className="w-full h-10 px-3 rounded-xl border border-emerald-100 text-sm outline-none focus:border-emerald-300"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setDateFrom('');
                            setDateTo('');
                          }}
                          className={`mt-3 text-xs font-semibold text-emerald-700 ${focusRing}`}
                        >
                          Clear dates
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Honest about scope: filters run over the orders loaded here. */}
              {state.status === 'success' && hasMoreThanLoaded && (
                <p className="text-xs text-ink-secondary mt-3">
                  Showing your {orders.length} most recent orders of {total}. Search and date
                  filters apply to these.
                </p>
              )}

              {state.status === 'loading' && (
                <ul className="space-y-3 mt-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <OrderCardSkeleton key={index} />
                  ))}
                </ul>
              )}

              {state.status === 'error' && <div className="mt-4"><ErrorState message={state.error} onRetry={load} /></div>}

              {state.status === 'success' && orders.length === 0 && (
                <div className="mt-4">
                  <EmptyState
                    icon={Package}
                    title="No orders yet"
                    description="Once you place an order, it'll show up here."
                    action={{ label: 'Shop Categories', to: '/categories' }}
                  />
                </div>
              )}

              {state.status === 'success' && orders.length > 0 && visibleOrders.length === 0 && (
                <div className="mt-4 bg-white rounded-[18px] border border-[#E4EDE4] shadow-[0_8px_22px_-20px_rgba(15,51,24,0.35)] px-6 py-10 text-center">
                  <span className="mx-auto w-12 h-12 rounded-full bg-[#E9F5E6] text-emerald-700 flex items-center justify-center mb-3">
                    <SearchIcon size={20} aria-hidden="true" />
                  </span>
                  <p className="serif text-[20px] font-bold text-emerald-950 mb-1">No orders match these filters</p>
                  <p className="text-sm text-ink-secondary mb-4">
                    {query.trim()
                      ? `No order number contains “${query.trim()}”. Check the number or clear the search.`
                      : dateFrom || dateTo
                      ? 'No orders were placed in this date range. Try different dates.'
                      : 'No orders have this status yet. Try another status.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveStatus('all');
                      setQuery('');
                      setDateFrom('');
                      setDateTo('');
                    }}
                    className={`inline-flex items-center h-10 px-5 rounded-full bg-[linear-gradient(90deg,#2E8B47,#1E7A3C)] text-white text-sm font-bold shadow-[0_8px_18px_-8px_rgba(46,139,71,0.6)] ${focusRing}`}
                  >
                    Clear filters
                  </button>
                </div>
              )}

              {state.status === 'success' && visibleOrders.length > 0 && (
                <ul className="space-y-3.5 mt-4">
                  {visibleOrders.map((order) => (
                    <OrderCard key={order._id || order.saleId} order={order} />
                  ))}
                </ul>
              )}

              {state.status === 'success' && filtersActive && visibleOrders.length > 0 && (
                <p className="text-xs text-ink-secondary mt-3">
                  Showing {visibleOrders.length} of {orders.length} loaded orders.
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default OrderHistoryPage;
